// ════════════════════════════════════════════════════════════════════════════
// C.5a-1 — Revisión del período de descuento (miércoles a martes) y exportación .xls (Excel 97-2003) para Intellisis.
// Lógica pura, sin pantalla ni red: recibe los pedidos de api/comedor/historial (ya filtrados a pedidos completos por
// el backend) y la lista de api/empleados. Sólo lectura: no escribe nada en el backend.
//   Hoja1 (importación): sin encabezado; A = ID texto de 5 dígitos, B vacía, C = cantidad numérica.
//   Hoja2 (revisión):    ID | NOMBRE COMPLETO | TOTAL:
// Ambas ordenadas por ID. Sin exclusiones automáticas: RH revisa y retira a mano. Si algo no se puede conciliar
// sin adivinar (ID ambiguo, nombre histórico ambiguo, pedido repetido, cuadre distinto) se BLOQUEA la descarga.
// ════════════════════════════════════════════════════════════════════════════
import { resolverEstatusEmpleado } from '../utils/estatusEmpleado';

export const ZONA_COMEDOR = 'America/Monterrey';
export const ENCABEZADOS_REVISION = ['ID', 'NOMBRE COMPLETO', 'TOTAL:'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// Igual que idEmpleadoCanonico_ del backend: entero o texto de 1 a 5 dígitos → 5 dígitos; '00000' y lo demás → ''.
export function idCanonico(valor) {
  if (typeof valor === 'number') {
    if (!Number.isInteger(valor)) return '';
    valor = String(valor);
  }
  if (typeof valor !== 'string') return '';
  const texto = valor.trim();
  if (!/^\d{1,5}$/.test(texto)) return '';
  const canonico = ('00000' + texto).slice(-5);
  return canonico === '00000' ? '' : canonico;
}

// ── Fechas (YYYY-MM-DD; aritmética en UTC para no depender de la zona del navegador) ─────────────
const aUTC = ymd => { const [a, m, d] = ymd.split('-').map(Number); return new Date(Date.UTC(a, m - 1, d)); };
const deUTC = f => f.toISOString().slice(0, 10);
export const esFechaYMD = v => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && deUTC(aUTC(v)) === v;
export function sumarDias(ymd, n) { const f = aUTC(ymd); f.setUTCDate(f.getUTCDate() + n); return deUTC(f); }
export const diaSemana = ymd => aUTC(ymd).getUTCDay(); // 0 = domingo … 3 = miércoles

// fecha_entrega llega como 'YYYY-MM-DD' o como fecha serializada ('2026-10-21T06:00:00.000Z'): se toma el día en la
// zona del comedor, igual que parseFechaComedor del backend. Cualquier otra cosa → ''.
export function fechaPedido(valor) {
  const texto = String(valor === undefined || valor === null ? '' : valor).trim();
  if (esFechaYMD(texto)) return texto;
  if (!/^\d{4}-\d{2}-\d{2}T/.test(texto)) return '';
  const f = new Date(texto);
  if (isNaN(f.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA_COMEDOR, year: 'numeric', month: '2-digit', day: '2-digit' }).format(f);
}

// Período de descuento: siempre de miércoles a martes. Devuelve el miércoles en o antes de la fecha dada.
export function inicioPeriodo(ymd) { return sumarDias(ymd, -((diaSemana(ymd) + 4) % 7)); }
export function periodoDe(ymd) {
  const inicio = inicioPeriodo(ymd);
  const dias = Array.from({ length: 7 }, (_, i) => sumarDias(inicio, i));
  return { inicio, fin: dias[6], dias };
}

// «14 al 20 de octubre de 2026», «28 de octubre al 3 de noviembre de 2026», «30 de diciembre de 2026 al 5 de enero de 2027».
export function etiquetaPeriodo(inicio, fin) {
  const [ai, mi, di] = inicio.split('-').map(Number), [af, mf, df] = fin.split('-').map(Number);
  if (ai !== af) return `${di} de ${MESES[mi - 1]} de ${ai} al ${df} de ${MESES[mf - 1]} de ${af}`;
  if (mi !== mf) return `${di} de ${MESES[mi - 1]} al ${df} de ${MESES[mf - 1]} de ${af}`;
  return `${di} al ${df} de ${MESES[mf - 1]} de ${af}`;
}
export const nombreArchivoIntellisis = (inicio, fin) => `Descuento ${etiquetaPeriodo(inicio, fin)}.xls`;

const nombreLimpio = v => String(v === undefined || v === null ? '' : v).replace(/\s+/g, ' ').trim();
const claveNombre = v => nombreLimpio(v).toLocaleUpperCase('es');

// ── Revisión del período ─────────────────────────────────────────────────────
// Cuenta los pedidos ACTIVOS cuya fecha cae en el período; los cancelados no cuentan. Devuelve las filas por ID
// (ordenadas), los avisos (para revisión de RH, no impiden descargar) y los bloqueos (impiden descargar).
// Nombre: el de EMPLEADOS; si el ID no está ahí, el del padrón de comedor (etapa temporal); si tampoco, el histórico
// del pedido. El padrón sólo aporta el nombre: ningún consumo se agrega ni se excluye por estar o no en él.
export function construirRevision({ pedidos, empleados, padron, inicio }) {
  if (!esFechaYMD(inicio) || diaSemana(inicio) !== 3) throw new Error('El período debe iniciar en miércoles');
  const { fin, dias } = periodoDe(inicio);
  const bloqueos = [], avisos = [];

  // EMPLEADOS por ID canónico; dos renglones con el mismo ID → ambiguo (sólo bloquea si ese ID tiene pedidos).
  const porId = new Map();
  (empleados || []).forEach(e => {
    const id = idCanonico(e && e.id);
    if (!id) return;
    if (!porId.has(id)) porId.set(id, []);
    porId.get(id).push(e);
  });

  const enPadron = new Map();
  (padron || []).forEach(p => { if (p && typeof p.id_empleado === 'string' && /^\d{5}$/.test(p.id_empleado)) enPadron.set(p.id_empleado, nombreLimpio(p.nombre_completo)); });

  const vistos = new Set(), grupos = new Map();
  let cancelados = 0, considerados = 0;
  (pedidos || []).forEach(p => {
    const fecha = fechaPedido(p && p.fecha_entrega);
    if (!fecha) { bloqueos.push({ tipo: 'FECHA_INVALIDA', id_pedido: String(p && p.id_pedido || ''), texto: 'Pedido con fecha de entrega no reconocible' }); return; }
    if (fecha < inicio || fecha > fin) return;
    if (p.estado === 'cancelado') { cancelados++; return; }
    if (p.estado !== 'activo') { bloqueos.push({ tipo: 'ESTADO_DESCONOCIDO', id_pedido: String(p.id_pedido || ''), texto: 'Pedido con estado no reconocido' }); return; }
    const idPedido = String(p.id_pedido || '').trim();
    if (!idPedido || vistos.has(idPedido)) { bloqueos.push({ tipo: 'PEDIDO_REPETIDO', id_pedido: idPedido, texto: 'Pedido sin número o repetido: se contaría dos veces' }); return; }
    vistos.add(idPedido);
    const id = idCanonico(p.id_empleado);
    if (!id) { bloqueos.push({ tipo: 'ID_INVALIDO', id_pedido: idPedido, texto: 'Pedido con ID de empleado que no es de 5 dígitos' }); return; }
    considerados++;
    if (!grupos.has(id)) grupos.set(id, { id, porDia: {}, nombres: new Map(), formas: new Set() });
    const g = grupos.get(id);
    g.porDia[fecha] = (g.porDia[fecha] || 0) + 1;
    g.formas.add(String(p.id_empleado).trim());
    const n = nombreLimpio(p.nombre_empleado);
    if (n && !g.nombres.has(claveNombre(n))) g.nombres.set(claveNombre(n), n);
  });

  const filas = [...grupos.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)).map(g => {
    const total = Object.values(g.porDia).reduce((s, n) => s + n, 0);
    const fila = { id: g.id, nombre: '', origenNombre: '', total, porDia: g.porDia };
    const enEmpleados = porId.get(g.id) || [];
    if (enEmpleados.length > 1) {
      bloqueos.push({ tipo: 'ID_AMBIGUO', id: g.id, texto: `El ID aparece ${enEmpleados.length} veces en EMPLEADOS` });
    } else if (enEmpleados.length === 1) {
      const e = enEmpleados[0];
      fila.nombre = nombreLimpio(e.nombre); fila.origenNombre = 'EMPLEADOS';
      if (!fila.nombre) bloqueos.push({ tipo: 'SIN_NOMBRE', id: g.id, texto: 'El empleado no tiene nombre en EMPLEADOS' });
      const est = resolverEstatusEmpleado(null, e);
      if (est.valor !== 'Activo') avisos.push({ tipo: 'NO_ACTIVO', id: g.id, texto: `Estatus en EMPLEADOS: ${est.texto}` });
    } else if (enPadron.get(g.id)) {
      fila.nombre = enPadron.get(g.id); fila.origenNombre = 'PADRON';
      avisos.push({ tipo: 'SOLO_EN_PADRON', id: g.id, texto: 'No está en EMPLEADOS: se usa el nombre del padrón de comedor' });
    } else {
      const historicos = [...g.nombres.values()];
      if (historicos.length === 1) {
        fila.nombre = historicos[0]; fila.origenNombre = 'PEDIDO';
        avisos.push({ tipo: 'NO_EN_EMPLEADOS', id: g.id, texto: 'No está en EMPLEADOS: se usa el nombre histórico del pedido' });
      } else if (historicos.length === 0) {
        bloqueos.push({ tipo: 'SIN_NOMBRE', id: g.id, texto: 'No está en EMPLEADOS y sus pedidos no tienen nombre' });
      } else {
        bloqueos.push({ tipo: 'NOMBRE_AMBIGUO', id: g.id, texto: `No está en EMPLEADOS y sus pedidos traen ${historicos.length} nombres distintos` });
      }
    }
    const otrasFormas = [...g.formas].filter(f => f !== g.id);
    if (otrasFormas.length) avisos.push({ tipo: 'ID_SIN_CEROS', id: g.id, texto: `Hay pedidos guardados como ${otrasFormas.join(', ')}: se cuentan como ${g.id}` });
    const maxDia = Math.max(...Object.values(g.porDia));
    if (maxDia > 2) avisos.push({ tipo: 'MAS_DE_DOS', id: g.id, texto: `Tiene ${maxDia} comidas activas en un mismo día (máximo 2)` });
    else if (maxDia === 2) avisos.push({ tipo: 'DOS_EN_UN_DIA', id: g.id, texto: 'Tiene 2 comidas activas en un mismo día' });
    return fila;
  });

  const total = filas.reduce((s, f) => s + f.total, 0);
  if (total !== considerados) bloqueos.push({ tipo: 'CUADRE', texto: `La suma por ID (${total}) no coincide con los pedidos activos (${considerados})` });
  return { inicio, fin, dias, filas, total, pedidosActivos: considerados, cancelados, avisos, bloqueos };
}

// ── Libro .xls ───────────────────────────────────────────────────────────────
const celdaId = id => ({ t: 's', v: id, z: '@' });

// Arma el libro con SheetJS (se recibe para no atar este módulo a la librería). Se niega con bloqueos o sin filas.
export function libroIntellisis(revision, XLSX) {
  if (revision.bloqueos.length) throw new Error('La revisión tiene bloqueos: no se genera el archivo');
  if (!revision.filas.length) throw new Error('No hay pedidos activos en el período');
  revision.filas.forEach(f => { if (idCanonico(f.id) !== f.id || !Number.isInteger(f.total) || f.total < 1) throw new Error('Fila inválida: ' + f.id); });

  const hoja1 = {}, hoja2 = {};
  revision.filas.forEach((f, r) => {
    hoja1[XLSX.utils.encode_cell({ r, c: 0 })] = celdaId(f.id);
    hoja1[XLSX.utils.encode_cell({ r, c: 2 })] = { t: 'n', v: f.total };
  });
  hoja1['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: revision.filas.length - 1, c: 2 } });

  ENCABEZADOS_REVISION.forEach((h, c) => { hoja2[XLSX.utils.encode_cell({ r: 0, c })] = { t: 's', v: h }; });
  revision.filas.forEach((f, i) => {
    hoja2[XLSX.utils.encode_cell({ r: i + 1, c: 0 })] = celdaId(f.id);
    hoja2[XLSX.utils.encode_cell({ r: i + 1, c: 1 })] = { t: 's', v: f.nombre };
    hoja2[XLSX.utils.encode_cell({ r: i + 1, c: 2 })] = { t: 'n', v: f.total };
  });
  hoja2['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: revision.filas.length, c: 2 } });

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja1, 'Hoja1');
  XLSX.utils.book_append_sheet(libro, hoja2, 'Hoja2');
  return libro;
}

// Bytes del archivo en formato Excel 97-2003 (BIFF8), no un .xlsx renombrado.
export const bytesXls = (libro, XLSX) => XLSX.write(libro, { bookType: 'xls', type: 'array' });
